import React from "react";
import {
  TextField,
  MenuItem,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { LabelM, SubtitleM } from "./Typography";
import JsonEditor from "./JsonEditor";

const LABELM_STYLE_EXT = { fontSize: "1rem", textAlign: "left", marginBottom: "8px" };
const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

/**
 * Reusable HTTP Tool Configuration Component
 * Used in both CapabilityForm (creating tools) and CapabilityFormSidebar (attaching tools to agents)
 *
 * @param {Object} config - Configuration values
 * @param {Function} onChange - Callback for configuration changes
 * @param {boolean} useAccordions - Whether to use accordions (for sidebars) or flat layout (for forms)
 * @param {boolean} showDescription - Whether to show description override field
 */
export default function HttpCapabilityConfiguration({
  config = {},
  onChange,
  useAccordions = false,
  showDescription = false,
  jsonErrors = {},
}) {
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  const renderField = (label, subtitle, component) => {
    if (useAccordions && (label === "Headers" || label === "Request Body" || label === "Output Schema")) {
      return (
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">{label}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {subtitle}
              </Typography>
            )}
            {component}
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        <LabelM style={LABELM_STYLE_EXT}>{label}</LabelM>
        {subtitle && (
          <SubtitleM style={{ marginBottom: "8px", color: "#666", fontSize: "0.85rem" }}>
            {subtitle}
          </SubtitleM>
        )}
        {component}
      </Box>
    );
  };

  return (
    <>
      {renderField(
        <>URL <span style={{ color: "#ef4444" }}>*</span></>,
        "API endpoint URL",
        <TextField
          fullWidth
          required
          value={config.url || ""}
          onChange={(e) => handleChange("url", e.target.value)}
          placeholder="https://api.example.com/endpoint"
        />
      )}

      {renderField(
        <>Method <span style={{ color: "#ef4444" }}>*</span></>,
        "HTTP method",
        <TextField
          select
          required
          fullWidth={useAccordions}
          value={config.method || "GET"}
          onChange={(e) => handleChange("method", e.target.value)}
          sx={{ width: useAccordions ? "100%" : "250px" }}
        >
          {HTTP_METHODS.map((method) => (
            <MenuItem key={method} value={method}>
              {method}
            </MenuItem>
          ))}
        </TextField>
      )}

      {renderField(
        "Headers (JSON)",
        "Request headers as JSON object",
        <JsonEditor
          value={config.headers || ""}
          onChange={(newString) => handleChange("headers", newString)}
          error={!!jsonErrors.headers}
          helperText={jsonErrors.headers ? `Invalid JSON: ${jsonErrors.headers}` : ""}
          height="120px"
        />
      )}

      {renderField(
        "Request Body (JSON)",
        "Request body template as JSON. Use {{variable}} for dynamic values.",
        <JsonEditor
          value={config.body || ""}
          onChange={(newString) => handleChange("body", newString)}
          error={!!jsonErrors.body}
          helperText={jsonErrors.body ? `Invalid JSON: ${jsonErrors.body}` : ""}
          height="160px"
        />
      )}

      {renderField(
        "Output Schema (JSON)",
        "Expected output format to help the agent understand the response",
        <JsonEditor
          value={config.output_schema || ""}
          onChange={(newString) => handleChange("output_schema", newString)}
          error={!!jsonErrors.output_schema}
          helperText={jsonErrors.output_schema ? `Invalid JSON: ${jsonErrors.output_schema}` : ""}
          height="200px"
        />
      )}

      {showDescription && renderField(
        "Tool Description Override",
        "Override the tool description to provide agent-specific context",
        <TextField
          fullWidth
          multiline
          rows={3}
          value={config.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Describe what this tool does in the context of this agent..."
        />
      )}
    </>
  );
}
